import { Users } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { UserPresence } from "@/hooks/useSessionPresence";

interface ActiveUsersProps {
  users: UserPresence[];
}

export const ActiveUsers = ({ users }: ActiveUsersProps) => {
  if (users.length === 0) {
    return null;
  }

  const getInitials = (user: UserPresence) => {
    if (user.name) {
      return user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return user.email.slice(0, 2).toUpperCase();
  };

  const getDisplayName = (user: UserPresence) => {
    return user.name || user.email.split('@')[0];
  };

  return (
    <TooltipProvider>
      <div className="flex items-center gap-2">
        <Users className="h-4 w-4 text-muted-foreground" />
        <div className="flex -space-x-2">
          {users.slice(0, 3).map((user) => (
            <Tooltip key={user.user_id}>
              <TooltipTrigger>
                <Avatar className="h-8 w-8 border-2 border-background ring-1 ring-primary/20 transition-transform hover:scale-110 hover:z-10">
                  {user.avatar_url && (
                    <AvatarImage src={user.avatar_url} alt={getDisplayName(user)} />
                  )}
                  <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
                    {getInitials(user)}
                  </AvatarFallback>
                </Avatar>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-sm font-medium">{getDisplayName(user)}</p>
                <p className="text-xs text-muted-foreground">Aktiv in dieser Session</p>
              </TooltipContent>
            </Tooltip>
          ))}
          {users.length > 3 && (
            <Tooltip>
              <TooltipTrigger>
                <Avatar className="h-8 w-8 border-2 border-background ring-1 ring-primary/20">
                  <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
                    +{users.length - 3}
                  </AvatarFallback>
                </Avatar>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-sm font-medium">
                  {users.length - 3} weitere {users.length - 3 === 1 ? 'Person' : 'Personen'}
                </p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};
